import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  Upload,
  message,
} from "antd";
import { RcFile, UploadFile } from "antd/es/upload";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { InfiniteScrollSelect } from "~/components/common";
import { useGetMyBookingsInfinite } from "~/services/booking";
import { CreateFileDto, useCreateFile } from "~/services/file";
import { useGetPresignedUrl } from "~/services/upload";
import { useAuth } from "~/store";
import { DocumentStatus, DocumentType } from "~/utils/constants";

const { Text } = Typography;
const { Option } = Select;

interface DocumentFile extends RcFile {
  url?: string;
  preview?: string;
  originFileObj?: RcFile;
}

interface DocumentUploadModalProps {
  visible: boolean;
  documentType: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface DocumentFormValues {
  title: string;
  type: DocumentType;
  clientId?: string;
  jobTicketId?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  visible,
  documentType,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<DocumentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const getPresignedUrlMutation = useGetPresignedUrl();
  const createFileMutation = useCreateFile();
  const { user } = useAuth();

  useEffect(() => {
    if (visible && documentType) {
      form.setFieldsValue({
        type: documentType,
      });
    }
  }, [visible, documentType, form]);

  const handleUploadChange = ({ fileList }: any) => {
    // Only keep the last file (replacing previous selections)
    const latestFile = fileList.slice(-1);

    // Validate file type
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const filteredFiles = latestFile.filter((file: any) => {
      const isValidType = validTypes.includes(file.type);
      if (!isValidType) {
        message.error(
          "Please upload a valid document file (PDF, DOCX, JPG, PNG)"
        );
      }
      return isValidType;
    });

    setFileList(filteredFiles);
  };

  const handleSubmit = async (values: DocumentFormValues) => {
    if (fileList.length === 0) {
      message.error("Please select a document to upload");
      return;
    }

    setIsUploading(true);

    try {
      const file = fileList[0].originFileObj;
      if (!file) throw new Error("File not found");

      const presignedUrlData = await getPresignedUrlMutation.mutateAsync({
        fileName: file.name,
        mimeType: file.type,
      });

      await axios.put(presignedUrlData.presignedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      const fileData: CreateFileDto = {
        title: values.title,
        type: values.type,
        status: DocumentStatus.SENT, // Default status
        filePath: presignedUrlData.url,
        clientId: values.clientId || user?.client?.id,
      };

      if (values.jobTicketId && values.jobTicketId !== "placeholder") {
        fileData.jobTicketId = values.jobTicketId;
      }

      await createFileMutation.mutateAsync(fileData);
      message.success("Document uploaded successfully");
      setFileList([]);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("Failed to upload document. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Upload Document"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: documentType,
        }}
      >
        <Form.Item
          name="title"
          label="Document Title"
          rules={[{ required: true, message: "Please enter document title" }]}
        >
          <Input placeholder="Enter document title" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Document Type"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value={DocumentType.INVENTORY}>Inventory</Option>
            <Option value={DocumentType.ADMINISTRATIVE}>Administrative</Option>
            <Option value={DocumentType.LEGAL}>Legal</Option>
            <Option value={DocumentType.SUPPLEMENTARY}>Supplementary</Option>
            <Option value={DocumentType.OTHER}>Other</Option>
          </Select>
        </Form.Item>

        <SelectBookings />

        <Form.Item
          label="Document File"
          rules={[{ required: true, message: "Please upload a document file" }]}
        >
          <Upload
            beforeUpload={() => false}
            onChange={handleUploadChange}
            fileList={fileList as UploadFile[]}
            maxCount={1}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
          <Text type="secondary" className="mt-2 block">
            Supported formats: PDF, DOCX, JPG, PNG (Max: 10MB)
          </Text>
        </Form.Item>

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUploading}
              disabled={fileList.length === 0}
            >
              Upload Document
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SelectBookings = () => {
  const infiniteBookings = useGetMyBookingsInfinite();
  return (
    <Form.Item name="jobTicketId" label="Bookings (Optional)">
      <InfiniteScrollSelect
        placeholder="Select booking"
        allowClear
        showSearch
        optionFilterProp="children"
        queryResult={infiniteBookings}
        getOptionLabel={(item) =>
          `${item.referenceNumber} - ${item.project? `${item.project.name}` : "No Project"}`
        }
        getOptionValue={(item) => item.id}
      />
    </Form.Item>
  );
};
